import { useEffect, useState } from "react";
import styled from "styled-components";
import { Col, Form, Row, Table } from "react-bootstrap";

import MyCheckbox from "common/components/atoms/MyCheckbox";
import PrimaryButton from "common/components/atoms/PrimaryButton";

import { TasksByTaskType } from "pages/print/types/TasksByTaskType";
import { TaskPrintTarget } from "../../types/TaskPrintTarget";
import { SelectPicturesPanel } from "./SelectPicturesPanel";
import { DateAndPictures, PrintTasksRequestInfo } from "pages/print/types/PrintTasksRequestInfo";

type Props = {
  tasks: TasksByTaskType[];
  printRequested: (targets: PrintTasksRequestInfo[]) => void;
};

export const SelectTasksPanel = (props: Props) => {
  const { tasks } = props;
  const [targets, setTargets] = useState<TaskPrintTarget[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<TaskPrintTarget>();
  const [allChecked, setAllChecked] = useState(false);
  const [canPrint, setCanPrint] = useState(false);

  /**
   * どれか一つのチェックの状態が変わった時の処理
   * 全体をチェックするチェックボックスの状態を変更する
   */
  const onCheckChanged = () => {
    setAllChecked(targets.every((x) => x.isSelected));
    setCanPrint(targets.some((x) => x.isSelected));
  };

  /**
   * 全体の変更をするチェックボックスのチェックが押された時の処理
   * 全ての行のチェックのオンオフを切り替える
   */
  const onAllChecked = (checked: boolean) => {
    targets.forEach((x) => (x.isSelected = checked));
    setAllChecked(checked);
    setCanPrint(checked);
  };

  const extractPictures = (task: TasksByTaskType): DateAndPictures[] =>
    task.tasks.map((x) => ({
      date: x.date,
      pictures: x.record.pictures.map((pict) => pict.fileId),
    }));

  const onPrintClick = () => {
    const printTargets = targets
      .filter((target) => target.isSelected)
      .map((x) => {
        return {
          taskId: x.taskData.taskType.id,
          dateAndPictures: x.selectedPictures.filter((pict) => pict.pictures.length > 0),
        } as PrintTasksRequestInfo;
      });
    props.printRequested(printTargets);
  };

  useEffect(() => {
    setTargets(
      tasks.map((task) => {
        return {
          isSelected: false,
          taskData: task,
          selectedPictures: extractPictures(task), // デフォルトは全選択
        };
      })
    );
    setSelectedTarget(undefined);
  }, [tasks]);

  return (
    <SDiv className="my-2 p-3">
      <Row>
        <Col sm={6}>
          <STitleDiv>
            <h4>出力対象を選択してください</h4>
            <PrimaryButton
              title="出力"
              size="sm"
              onClick={onPrintClick}
              className="mt-2"
              disabled={!canPrint}
            />
          </STitleDiv>
          <SScrollable>
            <STable bordered hover responsive>
              <thead>
                <tr>
                  <th>
                    <Scheck>
                      <Form.Check
                        checked={allChecked}
                        onChange={(e) => onAllChecked(e.currentTarget?.checked)}
                      />
                      印刷
                    </Scheck>
                  </th>
                  <th>作業名</th>
                  <th>登録回数</th>
                  <th>写真</th>
                </tr>
              </thead>
              <tbody>
                {targets.map((target) => (
                  <tr
                    key={`task_${target.taskData.taskType.id}`}
                    onClick={() => setSelectedTarget(target)}
                    className={`${target === selectedTarget ? "selectedRow" : ""}`}
                  >
                    <StdCenter style={{ width: "20px" }}>
                      <MyCheckbox
                        initialValue={target.isSelected}
                        changed={(checked) => {
                          target.isSelected = checked;
                          onCheckChanged();
                        }}
                      />
                    </StdCenter>
                    <td>{target.taskData.taskType.name}</td>
                    <td className="number">{target.taskData.tasks.length}</td>
                    <td className="number">
                      {target.taskData.tasks.reduce(
                        (prev, curr) => prev + curr.record.pictures.length,
                        0
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </STable>
          </SScrollable>
        </Col>
        <Col>
          <STitleDiv>
            <h4>写真を選択してください</h4>
          </STitleDiv>
          <SScrollable>
            <SPictDiv>
              {selectedTarget && (
                <>
                  <SelectPicturesPanel
                    task={selectedTarget.taskData}
                    selecteds={selectedTarget.selectedPictures}
                    selectionChanged={(fileIds) => {
                      const targ = targets.find(
                        (x) => x.taskData.taskType.id === selectedTarget.taskData.taskType.id
                      );
                      if (!targ) return;
                      targ.selectedPictures = fileIds;
                      setTargets([...targets]);
                      setSelectedTarget(targ);
                    }}
                  />
                </>
              )}
            </SPictDiv>
          </SScrollable>
        </Col>
      </Row>
    </SDiv>
  );
};

const SDiv = styled.div`
  box-shadow: 1px 1px 3px #888;
  align-self: stretch;
`;

const STitleDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  padding-bottom: 0.5rem;
  min-height: 45px;
`;

const SScrollable = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const STable = styled(Table)`
  th {
    text-align: center;
  }
  td.number {
    text-align: right;
  }
`;

const Scheck = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-width: 60px;
  @media screen and (max-width: 768px) {
    min-width: 0px;
  }
`;

const StdCenter = styled.td`
  text-align: center;
`;

const SPictDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  flex-wrap: wrap;
`;
